import { ts } from '../types';

export * from './promises';
export * from './filters';
export * from './std';
export * from './debug';
export * from './throttling';
export * from './strings';
export * from './arrays';

/** Only map the given `value` with the given `mapFn` if its value is not `null | undefined` */
export function maybeMap<T, R>(
    value: T,
    mapFn: (value: Exclude<T, undefined | null>) => R,
): R | Extract<T, undefined | null> {
    if (value === null || value === undefined) return value as any;
    return mapFn(value as any);
}

export function mapUnion<
    From extends string,
    To extends string | number | null | undefined,
    /** Some trick to make the `fallback` property not override the inferred `To` type */
    _Value extends NoInfer<Extract<To, string>> = NoInfer<Extract<To, string>>,
>(value: From, map: { [K in Extract<To, string>]: From | From[] }, fallback?: _Value): To {
    const result =
        Object.entries(map).find(([_key, options]) =>
            Array.isArray(options) ? options.includes(value) : options === value,
        )?.[0] ??
        fallback ??
        null;

    if (result === null) {
        throw new Error(`Failed to map "${value}" with config ${JSON.stringify(map)}`);
    }

    return result as To;
}

export function safeCall<T>(fn: () => T) {
    try {
        return {
            ok: true,
            value: fn(),
        } as const;
    } catch (error) {
        return {
            ok: false,
            error: error as Error,
        } as const;
    }
}

/** Works just like `React.SetStateAction`.
 * This type can be used in function parameters and either represents
 * - a function `T` that computes the value from a previous value
 * - an immediate value that's of type `ReturnType<T>`
 *
 * Simple type signature: `T | (...prev: any) => T` */
export type ComputedOrImmediate<T extends ComputedOrImmediate.AnyComputeFn> = ts.PhantomTag<
    ReturnType<T> | T,
    T
>;
export namespace ComputedOrImmediate {
    export type AnyComputeFn = (...args: any[]) => any;
    export type inferOutput<T extends ComputedOrImmediate<any>> =
        T extends ComputedOrImmediate<infer Fn> ? ReturnType<Fn> : never;

    /** Works just like `React.Dispatch<React.SetStateAction>`.
     * If the `computedOrImmediate` is a function, it will be executed with its needed parameters and return the result
     * Else the value is an immediate and that will be returned as the result */
    export function get<T extends ComputedOrImmediate.AnyComputeFn>(
        computedOrImmediate: ComputedOrImmediate<T>,
        ...args: Parameters<T>
    ): ReturnType<T> {
        return typeof computedOrImmediate === 'function'
            ? (computedOrImmediate as (...args: any) => any)(...args)
            : (computedOrImmediate as any);
    }
}
